import { useListState } from "@/hooks/use-list-state";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PARTNER_TYPES } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { FileText, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AgreementStats, AgreementSubmission } from "./types";

export function PartnerAgreementSubmissionsPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [statusFilter, setStatusFilter] = useListState<string>("partner-agreements:status", "pending");
  const [typeFilter, setTypeFilter] = useListState<string>("partner-agreements:type", "all");
  const [searchQuery, setSearchQuery] = useListState<string>("partner-agreements:search", "");
  const [detailSub, setDetailSub] = useState<AgreementSubmission | null>(null);
  const [reviewDialog, setReviewDialog] = useState<{
    open: boolean;
    action: "approve" | "reject" | "needs_more_info";
    sub: AgreementSubmission | null;
    notes: string;
  }>({ open: false, action: "approve", sub: null, notes: "" });

  const statsQuery = useQuery<AgreementStats>({
    queryKey: ["/api/partner-agreements/admin/stats"],
    queryFn: async () => {
      const res = await fetch("/api/partner-agreements/admin/stats", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load stats");
      return res.json();
    },
  });

  const query = useQuery<AgreementSubmission[]>({
    queryKey: ["/api/partner-agreements/admin/submissions", statusFilter, typeFilter, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (typeFilter && typeFilter !== "all") params.set("partnerType", typeFilter);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      const res = await fetch(`/api/partner-agreements/admin/submissions?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load submissions");
      return res.json();
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ id, action, notes }: { id: number; action: string; notes: string }) =>
      apiRequest("POST", `/api/partner-agreements/admin/submissions/${id}/review`, { action, notes }),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["/api/partner-agreements/admin/submissions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/partner-agreements/admin/stats"] });
      setReviewDialog(d => ({ ...d, open: false }));
      toast({ title: vars.action === "approve" ? "Submission approved" : vars.action === "reject" ? "Submission rejected" : "Follow-up requested" });
    },
    onError: (err: any) => toast({ title: "Review failed", description: parseErrorMessage(err, "Couldn't save your review. Please try again."), variant: "destructive" }),
  });

  const typeLabel = (t: string) => t.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  const statusColor = (s: string) => {
    if (s === "approved") return "bg-green-100 text-green-700";
    if (s === "rejected") return "bg-red-100 text-red-700";
    if (s === "needs_more_info") return "bg-amber-100 text-amber-700";
    return "bg-slate-100 text-slate-600";
  };

  const subs = query.data ?? [];
  const stats = statsQuery.data;

  function buildCsvUrl() {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    if (typeFilter && typeFilter !== "all") params.set("partnerType", typeFilter);
    if (searchQuery.trim()) params.set("search", searchQuery.trim());
    return `/api/partner-agreements/admin/submissions/csv?${params}`;
  }

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {[
            { label: "Total", value: stats.total, cls: "bg-slate-50 text-slate-700" },
            { label: "Pending", value: stats.pending, cls: "bg-amber-50 text-amber-700" },
            { label: "Approved", value: stats.approved, cls: "bg-green-50 text-green-700" },
            { label: "Rejected", value: stats.rejected, cls: "bg-red-50 text-red-700" },
            { label: "More Info", value: stats.needs_more_info, cls: "bg-blue-50 text-blue-700" },
          ].map(({ label, value, cls }) => (
            <div key={label} className={`rounded-lg p-3 text-center ${cls}`} data-testid={`stat-agreement-${label.toLowerCase()}`}>
              <div className="text-xl font-bold">{value}</div>
              <div className="text-xs opacity-70">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search name, email, entity…"
          className="h-8 text-xs border rounded-md px-2 w-[180px]"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          data-testid="input-agreement-search"
        />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="needs_more_info">Needs More Info</SelectItem>
          </SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[160px] h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={() => query.refetch()} className="h-8 text-xs">Refresh</Button>
        <Button size="sm" variant="outline" className="h-8 text-xs" asChild data-testid="button-export-agreements-csv">
          <a href={buildCsvUrl()} download>Export CSV</a>
        </Button>
        <span className="text-xs text-slate-500 ml-auto">{subs.length} submission{subs.length !== 1 ? "s" : ""}</span>
      </div>

      {query.isLoading && <div className="text-sm text-slate-500">Loading…</div>}
      {query.isError && <div className="text-sm text-red-500">Failed to load submissions.</div>}

      {!query.isLoading && subs.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-center text-sm text-slate-500">No submissions found.</CardContent>
        </Card>
      )}

      {subs.length > 0 && (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs">Name / Entity</TableHead>
                <TableHead className="text-xs">Type</TableHead>
                <TableHead className="text-xs">Signed</TableHead>
                <TableHead className="text-xs">Seq #</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subs.map(sub => (
                <TableRow key={sub.id} data-testid={`row-agreement-${sub.id}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{sub.fullName}</div>
                    {sub.entityName && <div className="text-xs text-slate-500">{sub.entityName}</div>}
                    <div className="text-xs text-slate-400">{sub.email}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className="text-xs bg-brand-muted text-brand border-none">{typeLabel(sub.partnerType)}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {new Date(sub.signedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-xs text-slate-600">#{sub.signatureSequence}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor(sub.status)}`}>
                      {sub.status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => setDetailSub(sub)}
                        data-testid={`button-detail-agreement-${sub.id}`}
                      >
                        Detail
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        asChild
                        data-testid={`button-view-agreement-${sub.id}`}
                      >
                        <a href={`/partners/view/${sub.uuid}`} target="_blank" rel="noopener noreferrer">
                          <FileText className="h-3 w-3 mr-1" /> View
                        </a>
                      </Button>
                      {sub.pdfObjectKey && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs"
                          asChild
                        >
                          <a href={`/api/partner-agreements/view/${sub.uuid}/pdf`} download>PDF</a>
                        </Button>
                      )}
                      {sub.status === "pending" && (
                        <>
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => setReviewDialog({ open: true, action: "approve", sub, notes: "" })}
                            data-testid={`button-approve-agreement-${sub.id}`}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="h-7 text-xs"
                            onClick={() => setReviewDialog({ open: true, action: "reject", sub, notes: "" })}
                            data-testid={`button-reject-agreement-${sub.id}`}
                          >
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => setReviewDialog({ open: true, action: "needs_more_info", sub, notes: "" })}
                          >
                            More Info
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Review dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={open => setReviewDialog(d => ({ ...d, open }))}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog.action === "approve" ? "Approve Agreement" :
               reviewDialog.action === "reject" ? "Reject Agreement" : "Request More Information"}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.sub?.fullName} — {reviewDialog.sub ? typeLabel(reviewDialog.sub.partnerType) : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">
                {reviewDialog.action === "approve" ? "Approval notes (optional)" : "Notes / reason (sent to applicant) *"}
              </label>
              <textarea
                className="mt-1 w-full border rounded-md text-sm p-2 min-h-[80px]"
                placeholder={reviewDialog.action === "approve"
                  ? "Optional notes…"
                  : "Explain why the application was rejected or what additional info is needed…"}
                value={reviewDialog.notes}
                onChange={e => setReviewDialog(d => ({ ...d, notes: e.target.value }))}
                data-testid="textarea-review-notes"
              />
            </div>
            {reviewDialog.action !== "approve" && !reviewDialog.notes.trim() && (
              <p className="text-xs text-amber-600">Notes are required for reject / needs-more-info actions.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialog(d => ({ ...d, open: false }))}>Cancel</Button>
            <Button
              onClick={() => {
                if (!reviewDialog.sub) return;
                if (reviewDialog.action !== "approve" && !reviewDialog.notes.trim()) return;
                reviewMutation.mutate({ id: reviewDialog.sub.id, action: reviewDialog.action, notes: reviewDialog.notes });
              }}
              disabled={reviewMutation.isPending}
              className={reviewDialog.action === "approve"
                ? "bg-green-600 hover:bg-green-700 text-white"
                : reviewDialog.action === "reject"
                  ? "bg-red-600 hover:bg-red-700 text-white"
                  : "bg-amber-600 hover:bg-amber-700 text-white"}
              data-testid="button-confirm-review"
            >
              {reviewMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {reviewDialog.action === "approve" ? "Approve & Notify" :
               reviewDialog.action === "reject" ? "Reject & Notify" : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin detail sheet — full consent/signature metadata, audit trail, hash verification */}
      <Sheet open={!!detailSub} onOpenChange={open => { if (!open) setDetailSub(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          {detailSub && (
            <>
              <SheetHeader className="pb-4 border-b">
                <SheetTitle className="text-brand">{detailSub.fullName}</SheetTitle>
                <SheetDescription className="flex items-center gap-2 flex-wrap">
                  <span className="bg-brand-muted text-brand text-xs px-2 py-0.5 rounded-full font-medium">
                    {typeLabel(detailSub.partnerType)}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(detailSub.status)}`}>
                    {detailSub.status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </span>
                  <span className="text-xs text-slate-400">Seq #{detailSub.signatureSequence}</span>
                </SheetDescription>
              </SheetHeader>

              <div className="space-y-5 pt-4">
                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Applicant Information</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <span className="text-slate-500">Email</span><span className="font-medium break-all">{detailSub.email}</span>
                    <span className="text-slate-500">Phone</span><span>{detailSub.phone}</span>
                    {detailSub.entityName && <><span className="text-slate-500">Entity</span><span>{detailSub.entityName}</span></>}
                    {detailSub.licenseNumber && <><span className="text-slate-500">License</span><span>{detailSub.licenseNumber}</span></>}
                    {detailSub.brokerageName && <><span className="text-slate-500">Brokerage</span><span>{detailSub.brokerageName}</span></>}
                    {detailSub.websiteUrl && <><span className="text-slate-500">Website</span><span className="break-all">{detailSub.websiteUrl}</span></>}
                    <span className="text-slate-500">Address</span>
                    <span>{detailSub.address}, {detailSub.city}, {detailSub.state} {detailSub.zip}</span>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Signature Metadata</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                    <span className="text-slate-500">Typed Signature</span>
                    <span className="font-semibold italic">{detailSub.typedName}</span>
                    <span className="text-slate-500">Signed At</span>
                    <span>{new Date(detailSub.signedAt).toLocaleString()}</span>
                    <span className="text-slate-500">Timezone</span>
                    <span>{detailSub.timezone}</span>
                    <span className="text-slate-500">IP Address</span>
                    <span className="font-mono text-xs">{detailSub.ipAddress || "—"}</span>
                    <span className="text-slate-500">User Agent</span>
                    <span className="text-xs break-all text-slate-600">{detailSub.userAgent || "—"}</span>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Consent Record</h3>
                  <div className="flex flex-wrap gap-2 text-xs">
                    {[
                      { label: "ESIGN Consent", v: detailSub.esignConsent },
                      { label: "Agreement Accepted", v: detailSub.agreementAccepted },
                      { label: "Read Complete", v: detailSub.agreementReadComplete },
                    ].map(({ label, v }) => (
                      <span key={label} className={`px-2 py-1 rounded-full font-medium ${v ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                        {v ? "✓" : "✗"} {label}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Integrity Verification</h3>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-2">
                    <span className="text-slate-500">Version</span><span>{detailSub.agreementVersion}</span>
                  </div>
                  <p className="text-xs text-slate-500 mb-1">SHA-256 Hash</p>
                  <p className="text-xs font-mono bg-slate-50 border rounded p-2 break-all text-slate-700" data-testid="text-admin-agreement-hash">
                    {detailSub.agreementHash}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    Public signed record:{" "}
                    <a href={`/partners/view/${detailSub.uuid}`} target="_blank" rel="noopener noreferrer" className="text-brand underline">view</a>
                  </p>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Signed Agreement Text</h3>
                  <pre className="text-xs text-slate-700 bg-slate-50 border rounded p-3 whitespace-pre-wrap break-words max-h-72 overflow-y-auto font-mono leading-relaxed" data-testid="text-admin-agreement-snapshot">
                    {detailSub.agreementSnapshot}
                  </pre>
                </div>

                <div>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Audit Trail</h3>
                  {(!detailSub.auditLog || detailSub.auditLog.length === 0) ? (
                    <p className="text-xs text-slate-400">No audit entries yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {detailSub.auditLog.map((entry, i) => (
                        <div key={i} className="bg-slate-50 border rounded p-2 text-xs" data-testid={`audit-entry-${i}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-brand">{String(entry.event ?? "event")}</span>
                            <span className="text-slate-400">{entry.ts ? new Date(String(entry.ts)).toLocaleString() : ""}</span>
                          </div>
                          {Object.entries(entry)
                            .filter(([k]) => k !== "event" && k !== "ts")
                            .map(([k, v]) => (
                              <div key={k} className="text-slate-600">
                                <span className="font-medium">{k}: </span>
                                <span className="break-all">{String(v ?? "")}</span>
                              </div>
                            ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {(detailSub.status === "pending" || detailSub.status === "needs_more_info") && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                      onClick={() => { setDetailSub(null); setReviewDialog({ open: true, action: "approve", sub: detailSub, notes: "" }); }}>
                      Approve
                    </Button>
                    <Button size="sm" variant="destructive"
                      onClick={() => { setDetailSub(null); setReviewDialog({ open: true, action: "reject", sub: detailSub, notes: "" }); }}>
                      Reject
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => { setDetailSub(null); setReviewDialog({ open: true, action: "needs_more_info", sub: detailSub, notes: "" }); }}>
                      More Info
                    </Button>
                  </div>
                )}
                {detailSub.reviewNotes && (
                  <div>
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Review Notes</h3>
                    <p className="text-sm text-slate-700 bg-slate-50 border rounded p-2">{detailSub.reviewNotes}</p>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
